import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import { get, del } from "../../helpers/api_helper";
import { useNavigate } from "react-router-dom";
import { CUSTOMER_LIST, DELETE_CUSTOMER } from "../../helpers/url_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css"; // Make sure this is imported for styling
import customerColumns from "components/Columns/CustomerColumns";
import EditCustomerModal from "./EditCustomerModal";
import { Tab } from "bootstrap";

const Customers = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAccountant, setIsAccountant] = useState(false);

  useEffect(() => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const role = Number(authUser?.user?.role);
      setIsAdmin(role === 1);
      setIsAccountant(role === 3);
    } catch {
      setIsAdmin(false);
    }
  }, []);
  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "customers", link: "#" },
  ];

  const [customers, setCustomers] = useState([]);
  const [pageIndex, setPageIndex] = useState(1); // Starts at 0
  const [pageSize, setPageSize] = useState(10); // Default page size
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);

  const navigate = useNavigate();

  async function getCustomersList(page, size, sort = [], search = "") {
    const sortKey = sort[0]?.id;
    const sortby = sort[0]?.desc ? "desc" : "asc";
    return await get(CUSTOMER_LIST, {
      params: {
        page,
        size,
        sortKey,
        sortby,
        search,
      },
    });
  }

  const fetchCustomers = async (
    page = 1,
    size = 10,
    sortBy = [],
    searchText
  ) => {
    try {
      const response = await getCustomersList(page, size, sortBy, searchText); // Assuming your API takes page & size
      const { items, totalPages } = response.data;

      setCustomers(items);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const handleSearchChange = (text) => {
    setPageIndex(1);
    setSearchText(text);
  };

  useEffect(() => {
    fetchCustomers(pageIndex, pageSize, sortBy, searchText);
  }, [pageIndex, pageSize, sortBy, searchText]);

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setEditModalOpen(true);
  };

  const handleTransaction = (customer) => {
    console.log(customer);
    setTransactionModalOpen(true);
    setSelectedCustomer(customer);
  };
  const [editModalOpen, setEditModalOpen] = useState(false);

  const handleDelete = (customer) => {
    setSelectedCustomer(customer); // Set the customer to be deleted
    setModalOpen(true); // Open the modal
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen); // Close the modal
  };

  const confirmDelete = async () => {
    try {
      await del(DELETE_CUSTOMER(selectedCustomer.ul_id)); // Delete the customer
      toastr.success("Customer deleted successfully!");
      fetchCustomers(pageIndex, pageSize, sortBy, searchText);
    } catch (error) {
      console.error(
        "Failed to delete customer:",
        error.response?.data || error.message
      );
      toastr.error("Something went wrong while deleting.");
    }
    setModalOpen(false); // Close the modal after the operation
  };

  const columns = useMemo(
    () =>
      customerColumns(
        isAdmin || isAccountant ? handleEdit : null,
        isAdmin ? handleDelete : null,
        isAdmin ? handleTransaction : null
      ),
    [isAdmin, isAccountant]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="CUstomer" breadcrumbItems={breadcrumbItems} />
        </Container>

        <Card>
          <CardBody>
            {customers.length > 0 && (
              <TableContainer
                columns={columns || []}
                data={customers || []}
                isPagination={true}
                isSortable={true}
                isGlobalFilter={true}
                customPageSize={pageSize}
                isAddOptions={isAdmin}
                addOptionTitle={"Add New Customer"}
                iscustomPageSize={false}
                isBordered={false}
                controlledPagination={{
                  pageIndex,
                  setPageIndex,
                  pageSize,
                  setPageSize,
                  totalPages,
                }}
                onSortChange={setSortBy}
                searchText={searchText}
                onSearchChange={handleSearchChange}
                handleAddClick={
                  isAdmin ? () => navigate("/add-customer") : undefined
                }
              />
            )}
          </CardBody>
        </Card>
      </div>
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this customer permanently?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </ModalFooter>
      </Modal>
      <EditCustomerModal
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(false)}
        customerData={selectedCustomer}
        onUpdated={() =>
          fetchCustomers(pageIndex, pageSize, sortBy, searchText)
        }
        isAdmin={isAdmin}
        isAccountant={isAccountant}
      />
      <Modal
        isOpen={transactionModalOpen}
        toggle={() => setTransactionModalOpen(false)}
        size="xl" // expands modal width
      >
        <ModalHeader toggle={() => setTransactionModalOpen(false)}>
          Customer Transactions
        </ModalHeader>
        <ModalBody>
          {/* Table for customer transactions */}
          <p>Transactions for customer: {selectedCustomer?.name || "N/A"}</p>

          <Table bordered striped responsive>
            <thead>
              <tr>
                <th>id</th>
                <th>Remaining Amt.</th>
                <th>Paid odr.</th>
                <th>Partial odr.</th>
                <th>Pending odr.</th>
                <th>Complete odr.</th>
                <th>Partial odr.</th>
                <th>Pending odr.</th>
                <th>Amt.</th>
                <th>Date</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              {selectedCustomer?.CustomerTransactions?.map((row, index) => {
                let meta = {};
                try {
                  meta = JSON.parse(row.meta || "{}");
                } catch (e) {
                  console.error("Invalid meta JSON", e);
                }

                return (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td>{row.remaining_amount}</td>
                    <td>{meta.paid_orders || 0}</td>
                    <td>{meta.partial_paid_orders || 0}</td>
                    <td>{meta.pendingOrderIds?.length || 0}</td>
                    <td>{(meta.completeOrderIds || []).join(", ")}</td>
                    <td>{(meta.partialOrderIds || []).join(", ")}</td>
                    <td>{(meta.pendingOrderIds || []).join(", ")}</td>
                    <td>
                      {row?.transaction_type === 1 ? (
                          <p className="text-success">{row.credit_amount}</p>
                        ) : (
                          <p className="text-danger">{row.debit_amount}</p>
                        )
                      }
                    </td>
                    <td>{row?.transaction_type === 1 ? new Date(row.credited_date).toISOString().split("T")[0] : new Date(row.debit_date).toISOString().split("T")[0]}</td>
                    <td>
                      <div className="flex align-items-center">
                        <p>{row?.User?.name}</p>
                        <p>
                          {new Date(row.createdAt).toISOString().split("T")[0]}
                        </p>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default Customers;
